import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { DefaultChecklistActions, OrganizationChecklistActions } from '../actions/checklist.actions';
import { ChecklistService } from '../services/checklist.service';

@Injectable()
export class OrganizationCheckListEffects {
    constructor(private actions$: Actions, private checklistService: ChecklistService) {}

    loadOrganizationChecklists$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrganizationChecklistActions.loadOrganizationChecklists),
            mergeMap(() =>
                this.checklistService.getOrganizationChecklists().pipe(
                    map((checklists) => OrganizationChecklistActions.loadOrganizationChecklistsSuccess({ checklists })),
                    catchError((error) =>
                        of(OrganizationChecklistActions.loadOrganizationChecklistsFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    createChecklist$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrganizationChecklistActions.createChecklist),
            mergeMap((action) =>
                this.checklistService.addOrganizationChecklist(action.createChecklistDto).pipe(
                    map((checklist) => OrganizationChecklistActions.createChecklistSuccess({ checklist })),
                    catchError((error) => of(OrganizationChecklistActions.createChecklistFailure({ error }))),
                ),
            ),
        ),
    );

    updateChecklist$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrganizationChecklistActions.updateChecklist),
            mergeMap((action) =>
                this.checklistService.updateOrganizationChecklist(action.id, action.updateChecklistDto).pipe(
                    map((checklist) => OrganizationChecklistActions.updateChecklistSuccess({ checklist })),
                    catchError((error) => of(OrganizationChecklistActions.updateChecklistFailure({ error }))),
                ),
            ),
        ),
    );

    loadChecklistById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrganizationChecklistActions.loadChecklistById),
            mergeMap((action) =>
                this.checklistService.getOrganizationChecklistById(action.id).pipe(
                    map((checklist) => OrganizationChecklistActions.loadChecklistByIdSuccess({ checklist })),
                    catchError((error) => of(OrganizationChecklistActions.loadChecklistByIdFailure({ error }))),
                ),
            ),
        ),
    );

    deleteOrganizationChecklists$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OrganizationChecklistActions.deleteChecklists),
            mergeMap(({ ids }) =>
                this.checklistService.deleteOrganizationChecklist(ids).pipe(
                    map(() => OrganizationChecklistActions.deleteChecklistsSuccess({ ids })),
                    catchError((error) => of(OrganizationChecklistActions.deleteChecklistsFailure({ error }))),
                ),
            ),
        ),
    );

    loadDefaultChecklists$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DefaultChecklistActions.loadChecklists),
            mergeMap(() =>
                this.checklistService.getDefaultChecklists().pipe(
                    map((checklists) => DefaultChecklistActions.loadChecklistsSuccess({ checklists })),
                    catchError((error) => of(DefaultChecklistActions.loadChecklistsFailure({ error }))),
                ),
            ),
        ),
    );

    createDefaultChecklist$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DefaultChecklistActions.createChecklist),
            mergeMap((action) =>
                this.checklistService.addDefaultChecklist(action.createDefaultChecklistDto).pipe(
                    map((checklist) => DefaultChecklistActions.createChecklistSuccess({ checklist })),
                    catchError((error) => of(DefaultChecklistActions.createChecklistFailure({ error }))),
                ),
            ),
        ),
    );

    updateDefaultChecklist$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DefaultChecklistActions.updateChecklist),
            mergeMap((action) =>
                this.checklistService.updateDefaultChecklist(action.id, action.updateDefaultChecklistDto).pipe(
                    map((checklist) => DefaultChecklistActions.updateChecklistSuccess({ checklist })),
                    catchError((error) => of(DefaultChecklistActions.updateChecklistFailure({ error }))),
                ),
            ),
        ),
    );

    loadDefaultChecklistById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DefaultChecklistActions.loadChecklistById),
            mergeMap((action) =>
                this.checklistService.getDefaultChecklistById(action.id).pipe(
                    map((checklist) => DefaultChecklistActions.loadChecklistByIdSuccess({ checklist })),
                    catchError((error) => of(DefaultChecklistActions.loadChecklistByIdFailure({ error }))),
                ),
            ),
        ),
    );

    deleteDefaultChecklists$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DefaultChecklistActions.deleteChecklists),
            mergeMap(({ ids }) =>
                this.checklistService.deleteDefaultChecklists(ids).pipe(
                    map(() => DefaultChecklistActions.deleteChecklistsSuccess({ ids })),
                    catchError((error) => of(DefaultChecklistActions.deleteChecklistsFailure({ error }))),
                ),
            ),
        ),
    );

    getTranslations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DefaultChecklistActions.getChecklistsForTranslations),
            mergeMap(({ locales, untranslatedOnly }) =>
                this.checklistService.getDefaultChecklistsTranslations$(locales, untranslatedOnly).pipe(
                    map((response) => DefaultChecklistActions.getChecklistsForTranslationsSuccess({ data: response })),
                    catchError((error) => of(DefaultChecklistActions.getChecklistsForTranslationsFailure({ error }))),
                ),
            ),
        ),
    );

    updateTranslations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DefaultChecklistActions.updateChecklistTranslation),
            mergeMap(({ checklists }) =>
                this.checklistService.updateDefaultChecklistsTranslations(checklists).pipe(
                    map((response) =>
                        DefaultChecklistActions.updateChecklistTranslationSuccess({ checklists: response }),
                    ),
                    catchError((error) => of(DefaultChecklistActions.updateChecklistTranslationFailure({ error }))),
                ),
            ),
        ),
    );
}
